import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, retry, switchMap, takeUntil, filter, tap } from 'rxjs/operators';
import { RealEstateActions } from './real-estate.actions';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { environment } from '@env/environment';
import { debounceTime, distinctUntilChanged, from, of } from 'rxjs';
import {
  AdditionEntryDTO,
  EnergyContainer,
  FacadeContainer,
  OutRealEstateDTO,
  RealEstateDTO,
  RealEstateUsageEmbeddable,
  RebuildEstateDTO,
  RebuildEstimationOutputDTO,
  WithdrawalProductType,
  WithdrawDTO,
} from '@generated/generatedEntities';
import { CALCULATION_STATE, realEstatesFeature } from '@app/core/services/store/real-estate.state';
import _ from 'lodash';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HPPowerReturnObject, LcaBuildingElementPresetDTO } from '@generated/extended';
import { concatLatestFrom } from '@ngrx/operators';
import { SharedControllerFnService } from '@app/core/services/shared/shared-controller-fn.service';
import { TranslocoService } from '@jsverse/transloco';
import { Router } from '@angular/router';
@UntilDestroy()
@Injectable()
export class RealEstateEffects {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly actions$: Actions = inject(Actions);
  private sharedControllerFnService = inject(SharedControllerFnService);
  private _translocoService = inject(TranslocoService);
  private router = inject(Router);

  loadRealEstateDtos$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.getEmptyRealEstate),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectInitialRealEstate)),
      concatMap(([request, filters]) =>
        this.httpClient.get(`${environment.serverUrl}/api/rebuild_estate/empty`).pipe(
          untilDestroyed(this),
          map((realestate: any) => {
            // This is necessary in case of Debug routes for LCA/LCC
            if (this.router.url.includes('debug')) {
              const _debugInstanceRealestate = JSON.parse(sessionStorage.getItem('debugState') || '');
              return RealEstateActions.loadRealEstateSuccess(_debugInstanceRealestate);
            } else {
              realestate.metaData.withdrawalProductType = request.wpt;
              realestate.metaData.tasks = request.tasks.length === 0 ? [request.wpt] : request.tasks;
              this.postProcessEmptyRealEstateObject(realestate, request.wpt);
              return RealEstateActions.loadRealEstateSuccess(realestate);
            }
          }),
          catchError((error) => of(RealEstateActions.loadRealEstateFailure(error))),
        ),
      ),
    );
  });

  presetLCA$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        // RealEstateActions.estimateLCAEnergyNeeds,
        // RealEstateActions.estimateLCAEnergyNeedsAndPerformance,
        RealEstateActions.presetLCA,
      ),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateWithStatus)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),
      switchMap(([request, filters]) => {
        // console.warn(
        //   `presetLCA$ - filter status: ${JSON.stringify(filters.status)} filters realEstate: ${JSON.stringify(filters.realEstate)}`,
        // );
        if (
          filters.status === CALCULATION_STATE.CALCULATING ||
          filters.status === CALCULATION_STATE.FINAL_RESULT_LOADED
        ) {
          return of(RealEstateActions.noopDueToStatus());
        }

        //we don't want to send the current energy object to the backend - as we will make calculations based on the values there.
        const requestDTO = _.cloneDeep(filters.realEstate) as RebuildEstateDTO;
        const req = {
          realEstate: requestDTO,
          targetType: 'targetType' in request ? request.targetType : null,
        };
        let precondition1;
        let precondition2;
        let precondition3;
        let requestUrl;
        if (request.targetType == 'ANNEX') {
          requestUrl = `${environment.lcaPresetServiceUrl}/lca/preset/facade-anex`;
          // precondition1 =
          //   requestDTO.targetAnnex!.facadeTypes.length > 0 &&
          //   requestDTO.targetAnnex!.facadeTypes.filter((ft) => ft.facadeType != null).length > 0;
          precondition1 = requestDTO.targetAnnex!.facadeType != null;
          precondition2 = true;
          precondition3 = requestDTO.targetAnnex!.usages.filter((u) => u.type != null).length > 0;

          this.sharedControllerFnService.cleanUsages(req.realEstate.targetAnnex!.usages);
        } else if (request.targetType == 'ADDITION_STORY') {
          requestUrl = `${environment.lcaPresetServiceUrl}/lca/preset/facade-add-story`;
          // precondition1 =
          //   requestDTO.targetAdditionStory!.facadeTypes.length > 0 &&
          //   requestDTO.targetAdditionStory!.facadeTypes.filter((ft) => ft.facadeType != null).length > 0;
          precondition1 = requestDTO.targetAdditionStory!.facadeType != null;

          precondition2 = true;
          precondition3 = requestDTO.targetAdditionStory!.usages.filter((u) => u.type != null).length > 0;

          this.sharedControllerFnService.cleanUsages(req.realEstate.targetAdditionStory!.usages);
        } else if (request.targetType == 'OVERHAUL') {
          requestUrl = `${environment.lcaPresetServiceUrl}/lca/preset/facade-overhaul`;
          precondition1 = requestDTO.targetOverhaul!.facadeType != null;
          // precondition1 =
          //   requestDTO.targetOverhaul!.facadeTypes.length > 0 &&
          //   requestDTO.targetOverhaul!.facadeTypes.filter((ft) => ft.facadeType != null).length > 0;
          precondition2 = true;
          precondition3 = requestDTO.targetOverhaul!.usages.filter((u) => u.type != null).length > 0;
          this.sharedControllerFnService.cleanUsages(req.realEstate.targetOverhaul.usages);
        } else {
          requestUrl = `${environment.lcaPresetServiceUrl}/lca/preset/facade`;
          precondition1 =
            requestDTO.quality.facadeTypes.length > 0 &&
            requestDTO.quality.facadeTypes.filter((ft) => ft.facadeType != null).length > 0;

          precondition2 = requestDTO.quality.minergieStandard != null;
          precondition3 = requestDTO.usages.filter((u) => u.type != null).length > 0;
        }

        this.sharedControllerFnService.cleanUsages(req.realEstate.usages);

        if (precondition1 && precondition2 && precondition3) {
          return this.httpClient.post<LcaBuildingElementPresetDTO>(requestUrl, req).pipe(
            untilDestroyed(this),
            takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),
            map((data) => {
              // console.warn(data);
              if ('updateUvalueFor' in request && request.updateUvalueFor) {
                data.updateUvalueFor = request.updateUvalueFor;
              }
              return RealEstateActions.presetLCASuccess(data);
            }),
            catchError((error) => of(RealEstateActions.presetLCAError(error))),
          );
        } else {
          return of(RealEstateActions.presetLCANoop({ preconditions: [precondition1, precondition2, precondition3] }));
        }
      }),
    );
  });

  presetOverhaulLCA$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        // RealEstateActions.estimateLCAEnergyNeeds,
        // RealEstateActions.estimateLCAEnergyNeedsAndPerformance,
        RealEstateActions.presetOverhaulLCA,
      ),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateWithStatus)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),
      switchMap(([request, filters]) => {
        if (
          filters.status === CALCULATION_STATE.CALCULATING ||
          filters.status === CALCULATION_STATE.FINAL_RESULT_LOADED
        ) {
          return of(RealEstateActions.noopDueToStatus());
        }

        //we don't want to send the current energy object to the backend - as we will make calculations based on the values there.
        const requestDTO = _.cloneDeep(filters.realEstate) as RebuildEstateDTO;
        const req = {
          realEstate: requestDTO,
          targetType: 'targetType' in request ? request.targetType : null,
        };

        const precondition1 = requestDTO.targetOverhaul!.facadeType != null;
        // precondition1 =
        //   requestDTO.targetOverhaul!.facadeTypes.length > 0 &&
        //   requestDTO.targetOverhaul!.facadeTypes.filter((ft) => ft.facadeType != null).length > 0;
        // const precondition2 = !requestDTO.targetOverhaul!['minergieStandard'];
        const precondition2 = true;
        const precondition3 = requestDTO.targetOverhaul!.usages.filter((u) => u.type != null).length > 0;

        this.sharedControllerFnService.cleanUsages(req.realEstate.targetOverhaul.usages);

        if (precondition1 && precondition2 && precondition3) {
          return this.httpClient
            .post<LcaBuildingElementPresetDTO>(`${environment.lcaPresetServiceUrl}/lca/preset/facade-overhaul`, req)
            .pipe(
              untilDestroyed(this),
              takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),
              map((data) => {
                if (request.updateUvalueFor) {
                  data.updateUvalueFor = request.updateUvalueFor;
                }
                return RealEstateActions.presetLCASuccess(data);
              }),
              catchError((error) => of(RealEstateActions.presetLCAError(error))),
            );
        } else {
          return of(RealEstateActions.presetLCANoop({ preconditions: [precondition1, precondition2, precondition3] }));
        }
      }),
    );
  });

  presetLCAFu$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.presetLCAFunctionalUnits),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateWithStatus)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      switchMap(([request, filters]) => {
        // console.warn(
        //   `presetLCA$ - filter status: ${JSON.stringify(filters.status)} filters realEstate: ${JSON.stringify(filters.realEstate)}`,
        // );
        if (
          filters.status === CALCULATION_STATE.CALCULATING ||
          filters.status === CALCULATION_STATE.FINAL_RESULT_LOADED
        ) {
          return of(RealEstateActions.noopDueToStatus());
        }
        //we don't want to send the current energy object to the backend - as we will make calculations based on the values there.
        const requestDTO = _.cloneDeep(filters.realEstate) as RebuildEstateDTO;

        const req = {
          realEstate: requestDTO,
          targetType: 'targetType' in request ? request.targetType : null,
        };

        let precondition1;
        let precondition2;
        let requestUrl;

        if (request.targetType == 'ADDITION_STORY') {
          requestUrl = `${environment.lcaPresetServiceUrl}/lca/preset/fu-add-story`;
          precondition1 = requestDTO.targetAdditionStory!.energyReferenceArea >= 0;
          precondition2 = requestDTO.targetAdditionStory!.usages.filter((u) => u.type != null).length > 0;
          this.sharedControllerFnService.cleanUsages(requestDTO.targetAdditionStory!.usages);
        } else if (request.targetType == 'ANNEX') {
          requestUrl = `${environment.lcaPresetServiceUrl}/lca/preset/fu-annex`;
          precondition1 = requestDTO.targetAnnex!.energyReferenceArea >= 0;
          precondition2 = requestDTO.targetAnnex!.usages.filter((u) => u.type != null).length > 0;
          this.sharedControllerFnService.cleanUsages(requestDTO.targetAnnex!.usages);
        } else {
          requestUrl = `${environment.lcaPresetServiceUrl}/lca/preset/fu`;
          precondition1 = requestDTO.geometry.energyReferenceArea >= 0;
          precondition2 = requestDTO.usages.filter((u) => u.type != null).length > 0;
          this.sharedControllerFnService.cleanUsages(requestDTO.usages);
        }

        if (precondition1 && precondition2) {
          return this.httpClient.post<RealEstateUsageEmbeddable[]>(requestUrl, req).pipe(
            untilDestroyed(this),
            takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),
            map((data) => {
              if ('targetType' in request && request.targetType) {
                data[0].targetType = request.targetType;
              }
              console.warn('presetLCAFunctionalUnits', data);
              return RealEstateActions.presetLCAFunctionalUnitsSuccess({ data: data });
            }),
            catchError((error) => of(RealEstateActions.presetLCAFunctionalUnitsError(error))),
          );
        } else {
          return of(RealEstateActions.presetLCAFunctionalUnitsNoop({ preconditions: [precondition1, precondition2] }));
        }
      }),
    );
  });

  presetOverhaulLCAFu$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.presetLCAOverhaulFunctionalUnits),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateWithStatus)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      switchMap(([request, filters]) => {
        // console.warn(
        //   `presetLCA$ - filter status: ${JSON.stringify(filters.status)} filters realEstate: ${JSON.stringify(filters.realEstate)}`,
        // );
        if (
          filters.status === CALCULATION_STATE.CALCULATING ||
          filters.status === CALCULATION_STATE.FINAL_RESULT_LOADED
        ) {
          return of(RealEstateActions.noopDueToStatus());
        }
        //we don't want to send the current energy object to the backend - as we will make calculations based on the values there.
        const requestDTO = _.cloneDeep(filters.realEstate) as RebuildEstateDTO;

        const req = {
          realEstate: requestDTO,
          targetType: 'targetType' in request ? request.targetType : null,
        };

        let precondition1;
        let precondition2;

        precondition1 = requestDTO.targetOverhaul.energyReferenceArea >= 0;
        precondition2 = requestDTO.targetOverhaul.usages.filter((u) => u.type != null).length > 0;
        this.sharedControllerFnService.cleanUsages(requestDTO.targetOverhaul.usages);

        if (precondition1 && precondition2) {
          return this.httpClient
            .post<RealEstateUsageEmbeddable[]>(`${environment.lcaPresetServiceUrl}/lca/preset/fu-overhaul`, req)
            .pipe(
              untilDestroyed(this),
              takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),
              map((data) => {
                if ('targetType' in request && request.targetType) {
                  data[0].targetType = request.targetType;
                }

                console.warn('presetLCAFunctionalUnits', data);
                return RealEstateActions.presetLCAFunctionalUnitsSuccess({ data: data });
              }),
              catchError((error) => of(RealEstateActions.presetLCAFunctionalUnitsError(error))),
            );
        } else {
          return of(RealEstateActions.presetLCAFunctionalUnitsNoop({ preconditions: [precondition1, precondition2] }));
        }
      }),
    );
  });

  presetLCAPv$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.presetLCAFunctionalUnits, RealEstateActions.presetLCAPv),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateWithStatus)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      switchMap(([request, filters]) => {
        // console.warn(
        //   `presetLCA$ - filter status: ${JSON.stringify(filters.status)} filters realEstate: ${JSON.stringify(filters.realEstate)}`,
        // );
        if (
          filters.status === CALCULATION_STATE.CALCULATING ||
          filters.status === CALCULATION_STATE.FINAL_RESULT_LOADED
        ) {
          return of(RealEstateActions.noopDueToStatus());
        }
        //we don't want to send the current energy object to the backend - as we will make calculations based on the values there.
        const requestDTO = _.cloneDeep(filters.realEstate);

        if (requestDTO.quality.facadeTypes.length == 0 && requestDTO.quality.facadeType) {
          requestDTO.quality.facadeTypes = [{ facadeType: requestDTO.quality.facadeType } as FacadeContainer];
        }

        const precondition1 = requestDTO.geometry.energyReferenceArea >= 0;
        const precondition2 = requestDTO.usages.filter((u) => u.type != null).length > 0;
        this.sharedControllerFnService.cleanUsages(requestDTO.usages);

        if (precondition1 && precondition2) {
          return this.httpClient
            .post<{
              pvRoof: number;
              targetPvRoof: number | null;
            }>(`${environment.lcaPresetServiceUrl}/lca/preset/pv`, requestDTO)
            .pipe(
              untilDestroyed(this),
              takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),

              map((data) => {
                console.warn('presetLCAPvSuccess', data);
                return RealEstateActions.presetLCAPvSuccess(data);
              }),
              catchError((error) => of(RealEstateActions.presetLCAPvError(error))),
            );
        } else {
          return of(RealEstateActions.presetLCAFunctionalUnitsNoop({ preconditions: [precondition1, precondition2] }));
        }
      }),
    );
  });

  presetWpLCA$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RealEstateActions.presetLCAHPPower,
        RealEstateActions.estimateLCAEnergyNeedsAndPerformanceSuccess,
        RealEstateActions.estimateOverhaulLCAEnergyNeedsAndPerformanceSuccess,
      ),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateWithStatus)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      switchMap(([request, filters]) => {
        //we don't want to send the current energy object to the backend - as we will make calculations based on the values there.
        if (
          filters.status === CALCULATION_STATE.CALCULATING ||
          filters.status === CALCULATION_STATE.FINAL_RESULT_LOADED
        ) {
          return of(RealEstateActions.noopDueToStatus());
        }

        const requestDTO = _.cloneDeep(filters.realEstate);
        const req = {
          realEstateDTO: requestDTO,
          lcaTargetType: 'lcaTargetType' in request.data ? request.data.lcaTargetType : 'CURRENT', // We send CURRENT for the normal LCA
        };

        let precondition1 = false;
        let precondition2 = false;
        let precondition3 = false;

        if (requestDTO.metaData.tasks.includes('LCA') && requestDTO.metaData.tasks.includes('REBUILD_COSTS')) {
          if (req.lcaTargetType == 'CURRENT') {
            precondition1 =
              requestDTO.quality.heatingTypes.filter(
                (ht) =>
                  ht === 'HEAT_PUMP_GEOTHERMAL' ||
                  ht === 'HEAT_PUMP_AIR' ||
                  ht === 'HEAT_PUMP_GROUND_COLLECTOR' ||
                  ht === 'HEAT_PUMP_GROUND_WATER',
              ).length === 1;
          }
        } else {
          precondition1 =
            requestDTO.quality.heatingContainers.filter(
              (hc) =>
                hc.heatingType === 'HEAT_PUMP_GEOTHERMAL' ||
                hc.heatingType === 'HEAT_PUMP_AIR' ||
                hc.heatingType === 'HEAT_PUMP_GROUND_COLLECTOR' ||
                hc.heatingType === 'HEAT_PUMP_GROUND_WATER',
            ).length === 1;
        }

        if (req.lcaTargetType == 'CURRENT' || !req.lcaTargetType) {
          precondition2 =
            (requestDTO.energy.current.annualHeatingNeedsUserInput != null &&
              requestDTO.energy.current.annualHeatingNeedsUserInput >= 0) ||
            (requestDTO.energy.current.annualHeatingNeedsPlaceholder != null &&
              requestDTO.energy.current.annualHeatingNeedsPlaceholder >= 0);

          precondition3 =
            (requestDTO.energy.current.annualDhwNeedsUserInput != null &&
              requestDTO.energy.current.annualDhwNeedsUserInput >= 0) ||
            (requestDTO.energy.current.annualDhwNeedsPlaceholder != null &&
              requestDTO.energy.current.annualDhwNeedsPlaceholder >= 0);
        }

        this.sharedControllerFnService.cleanUsages(requestDTO.usages);

        if (precondition1 && precondition2 && precondition3) {
          return this.httpClient
            .post<HPPowerReturnObject>(`${environment.serverUrl}/api/withdrawal/preset/hp2`, req)
            .pipe(
              untilDestroyed(this),
              takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),
              map((data) => {
                return RealEstateActions.presetLCAHPPowerSuccess({ data });
              }),
              catchError((error) => of(RealEstateActions.presetLCAHPPowerError(error))),
            );
        } else {
          return of(
            RealEstateActions.presetLCAHPPowerNoop({ preconditions: [precondition1, precondition2, precondition3] }),
          );
        }
      }),
    );
  });

  presetOverhaulWpLCA$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RealEstateActions.presetOverhaulLCAHPPower,
        RealEstateActions.estimateOverhaulLCAEnergyNeedsAndPerformanceSuccess,
      ),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateWithStatus)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      switchMap(([request, filters]) => {
        //we don't want to send the current energy object to the backend - as we will make calculations based on the values there.
        if (
          filters.status === CALCULATION_STATE.CALCULATING ||
          filters.status === CALCULATION_STATE.FINAL_RESULT_LOADED
        ) {
          return of(RealEstateActions.noopDueToStatus());
        }

        const requestDTO = _.cloneDeep(filters.realEstate);
        const req = {
          realEstateDTO: requestDTO,
          lcaTargetType: 'lcaTargetType' in request.data ? request.data.lcaTargetType : null,
        };

        let precondition1 = false;
        let precondition2 = false;
        let precondition3 = false;

        if (requestDTO.metaData.tasks.includes('LCA') && requestDTO.metaData.tasks.includes('REBUILD_COSTS')) {
          if (req.lcaTargetType == 'OVERHAUL') {
            precondition1 =
              (requestDTO as RebuildEstateDTO).targetOverhaul.heatingTypes.filter(
                (ht: any) =>
                  ht === 'HEAT_PUMP_GEOTHERMAL' ||
                  ht === 'HEAT_PUMP_AIR' ||
                  ht === 'HEAT_PUMP_GROUND_COLLECTOR' ||
                  ht === 'HEAT_PUMP_GROUND_WATER',
              ).length === 1;
          }
          if ((requestDTO as RebuildEstateDTO).selectedTemplates.includes('ANNEX')) {
            precondition1 =
              precondition1 &&
              (requestDTO as RebuildEstateDTO).targetAnnex!.heatingTypes.includes('SHARED_OVERHAUL') &&
              (requestDTO as RebuildEstateDTO).targetOverhaul.heatingTypes.filter(
                (ht: any) =>
                  ht === 'HEAT_PUMP_GEOTHERMAL' ||
                  ht === 'HEAT_PUMP_AIR' ||
                  ht === 'HEAT_PUMP_GROUND_COLLECTOR' ||
                  ht === 'HEAT_PUMP_GROUND_WATER',
              ).length === 1;
          }
          if ((requestDTO as RebuildEstateDTO).selectedTemplates.includes('ADDITION_STORY')) {
            precondition1 =
              precondition1 &&
              (requestDTO as RebuildEstateDTO).targetAdditionStory!.heatingTypes.includes('SHARED_OVERHAUL') &&
              (requestDTO as RebuildEstateDTO).targetOverhaul.heatingTypes.filter(
                (ht: any) =>
                  ht === 'HEAT_PUMP_GEOTHERMAL' ||
                  ht === 'HEAT_PUMP_AIR' ||
                  ht === 'HEAT_PUMP_GROUND_COLLECTOR' ||
                  ht === 'HEAT_PUMP_GROUND_WATER',
              ).length === 1;
          }
        }

        if (req.lcaTargetType !== 'CURRENT') {
          precondition2 =
            (requestDTO.energy.target!.annualHeatingNeedsUserInput != null &&
              requestDTO.energy.target!.annualHeatingNeedsUserInput >= 0) ||
            (requestDTO.energy.target!.annualHeatingNeedsPlaceholder != null &&
              requestDTO.energy.target!.annualHeatingNeedsPlaceholder >= 0);

          precondition3 =
            (requestDTO.energy.target!.annualDhwNeedsUserInput != null &&
              requestDTO.energy.target!.annualDhwNeedsUserInput >= 0) ||
            (requestDTO.energy.target!.annualDhwNeedsPlaceholder != null &&
              requestDTO.energy.target!.annualDhwNeedsPlaceholder >= 0);

          this.sharedControllerFnService.cleanUsages((requestDTO as RebuildEstateDTO).targetOverhaul.usages);
          this.sharedControllerFnService.cleanUsages((requestDTO as RebuildEstateDTO).targetAdditionStory!.usages);
          this.sharedControllerFnService.cleanUsages((requestDTO as RebuildEstateDTO).targetAnnex!.usages);
        }

        this.sharedControllerFnService.cleanUsages(requestDTO.usages);

        if (precondition1 && precondition2 && precondition3) {
          return this.httpClient
            .post<HPPowerReturnObject>(`${environment.serverUrl}/api/withdrawal/preset/hp2`, req)
            .pipe(
              untilDestroyed(this),
              takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),
              map((data) => {
                return RealEstateActions.presetOverhaulLCAHPPowerSuccess({ data });
              }),
              catchError((error) => of(RealEstateActions.presetLCAHPPowerError(error))),
            );
        } else {
          return of(
            RealEstateActions.presetLCAHPPowerNoop({ preconditions: [precondition1, precondition2, precondition3] }),
          );
        }
      }),
    );
  });

  estimateLCA$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.estimateLCACosts),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateProgress)),
      switchMap(([request, filters]) => {
        var requestDTO = _.cloneDeep(filters) as RealEstateDTO;
        //TODO this should be somewhere else - but for now we do it here. If we keep those values in there we will print them - maybe it also makes sense to clean them in the backend?
        requestDTO.quality.exteriorWallsCladdingAboveTerrain =
          requestDTO.quality.exteriorWallsCladdingAboveTerrain.filter((x) => x.area != null);
        requestDTO.quality.windowsAboveTerrain = requestDTO.quality.windowsAboveTerrain.filter((x) => x.area != null);
        return this.httpClient
          .post<WithdrawDTO>(`${environment.serverUrl}/api/withdrawal/energy/estimate`, requestDTO)
          .pipe(
            untilDestroyed(this),
            map((withdrawDTO: any) => RealEstateActions.estimateLCACostsSuccess(withdrawDTO)),
            catchError((error, caught) => of(RealEstateActions.estimateLCACostsError({ error: caught }))),
          );
      }),
      catchError((error, caught) => of(RealEstateActions.estimateLCACostsError({ error: caught }))),
    );
  });

  presetEnergyNeeds$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.estimateLCAEnergyNeeds, RealEstateActions.estimateLCAEnergyNeedsAndPerformance),
      //we don't want to blast the preset API with calls - so we add a debounce to at least channel  few updates together - this happends mainly when loading a object - or rapid updates on the form.
      debounceTime(200),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateWithStatus)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      switchMap(([request, filters]) => {
        if (
          filters.status === CALCULATION_STATE.CALCULATING ||
          filters.status === CALCULATION_STATE.FINAL_RESULT_LOADED
        ) {
          return of(RealEstateActions.noopDueToStatus());
        }

        const requestDTO = _.cloneDeep(filters.realEstate);
        const req = {
          realEstateDTO: requestDTO,
          lcaTargetType: 'targetType' in request ? request.targetType : 'CURRENT', // We send CURRENT for the normal LCA
        };

        const precondition1 =
          requestDTO.quality.exteriorWallsCladdingAboveTerrain.filter(
            (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
          ).length > 0;

        let precondition2 = true;
        if (requestDTO.quality.roofTypes.includes('FLATROOF')) {
          precondition2 =
            requestDTO.quality.flatRoofsAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;
        }
        let precondition3 = true;
        if (requestDTO.quality.roofTypes.includes('INCLINEDROOF')) {
          precondition3 =
            requestDTO.quality.inclinedRoofsAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;
        }
        const precondition4 =
          requestDTO.quality.groundFloorSlabs.filter(
            (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
          ).length > 0;

        if (
          requestDTO.geometry.elevator != null &&
          requestDTO.geometry.elevator.vertical == null &&
          requestDTO.geometry.elevator.placeholder != null
        ) {
          requestDTO.geometry.elevator.vertical = requestDTO.geometry.elevator.placeholder;
        }
        let precondition5 = false;
        if ('targetType' in request && request.targetType) {
          precondition5 =
            requestDTO.energy.current.hotWaterFromHeating != null &&
            requestDTO.energy.target!.hotWaterFromHeating != null;
        } else {
          // In case of normal LCA we don't need the hotWaterFromHeating so we set this to true - otherwise the API will not be called
          precondition5 = true;
        }

        this.sharedControllerFnService.cleanUsages(requestDTO.usages);

        if (precondition1 && precondition2 && precondition3 && precondition4 && precondition5) {
          return this.httpClient
            .post<RebuildEstimationOutputDTO>(`${environment.serverUrl}/api/withdrawal/energy/needs2`, req)
            .pipe(
              untilDestroyed(this),
              takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),

              map((energyNeeds: RebuildEstimationOutputDTO) => {
                return RealEstateActions.estimateLCAEnergyNeedsSuccess(energyNeeds);
              }),
              catchError((error) => {
                console.warn('/api/withdrawal/energy/needs error: ', error);
                return of(RealEstateActions.estimateLCAEnergyNeedsFailure(error));
              }),
            );
        } else {
          return of(
            RealEstateActions.estimateLCAEnergyNeedsNoop({
              preconditions: [precondition1, precondition2, precondition3, precondition4],
            }),
          );
        }
      }),
    );
  });

  presetOverhaulEnergyNeeds$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RealEstateActions.estimateOverhaulLCAEnergyNeeds,
        RealEstateActions.estimateOverhaulLCAEnergyNeedsAndPerformance,
      ),
      //we don't want to blast the preset API with calls - so we add a debounce to at least channel  few updates together - this happends mainly when loading a object - or rapid updates on the form.
      debounceTime(200),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateWithStatus)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      switchMap(([request, filters]) => {
        if (
          filters.status === CALCULATION_STATE.CALCULATING ||
          filters.status === CALCULATION_STATE.FINAL_RESULT_LOADED
        ) {
          return of(RealEstateActions.noopDueToStatus());
        }

        const requestDTO = _.cloneDeep(filters.realEstate);
        const req = {
          realEstateDTO: requestDTO,
          lcaTargetType: 'targetType' in request ? request.targetType : 'OVERHAUL',
        };

        let precondition1 = false;
        let precondition2 = false;
        let precondition3 = false;
        let precondition4 = false;

        precondition1 =
          (requestDTO as RebuildEstateDTO).targetOverhaul!.exteriorWallsCladdingAboveTerrain.filter(
            (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
          ).length > 0;

        precondition2 = true;
        if ((requestDTO as RebuildEstateDTO).targetOverhaul.roofTypes.includes('FLATROOF')) {
          precondition2 =
            (requestDTO as RebuildEstateDTO).targetOverhaul.flatRoofsAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;
        }
        precondition3 = true;
        if ((requestDTO as RebuildEstateDTO).targetOverhaul.roofTypes.includes('INCLINEDROOF')) {
          precondition3 =
            (requestDTO as RebuildEstateDTO).targetOverhaul.inclinedRoofsAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;
        }
        precondition4 =
          (requestDTO as RebuildEstateDTO).targetOverhaul.groundFloorSlabs.filter(
            (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
          ).length > 0;

        this.sharedControllerFnService.cleanUsages((requestDTO as RebuildEstateDTO).targetOverhaul.usages);

        if (
          (requestDTO as RebuildEstateDTO).targetOverhaul['elevatorInside'] != null &&
          (requestDTO as RebuildEstateDTO).targetOverhaul['elevatorInside'].vertical == null &&
          (requestDTO as RebuildEstateDTO).targetOverhaul['elevatorInside'].placeholder != null
        ) {
          (requestDTO as RebuildEstateDTO).targetOverhaul['elevatorInside'].vertical = (
            requestDTO as RebuildEstateDTO
          ).targetOverhaul['elevatorInside'].placeholder;
        }
        if ((requestDTO as RebuildEstateDTO).selectedTemplates.includes('ADDITION_STORY')) {
          precondition1 =
            precondition1 &&
            (requestDTO as RebuildEstateDTO).targetAdditionStory!.exteriorWallsCladdingAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;

          // precondition2 = true;
          if ((requestDTO as RebuildEstateDTO).targetAdditionStory!.roofTypes.includes('FLATROOF')) {
            precondition2 =
              precondition2 &&
              (requestDTO as RebuildEstateDTO).targetAdditionStory!.flatRoofsAboveTerrain.filter(
                (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
              ).length > 0;
          }
          // precondition3 = true;
          if ((requestDTO as RebuildEstateDTO).targetAdditionStory!.roofTypes.includes('INCLINEDROOF')) {
            precondition3 =
              precondition3 &&
              (requestDTO as RebuildEstateDTO).targetAdditionStory!.inclinedRoofsAboveTerrain.filter(
                (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
              ).length > 0;
          }
          // precondition4 = true;

          this.sharedControllerFnService.cleanUsages((requestDTO as RebuildEstateDTO).targetAdditionStory!.usages);
        }
        if ((requestDTO as RebuildEstateDTO).selectedTemplates.includes('ANNEX')) {
          precondition1 =
            precondition1 &&
            (requestDTO as RebuildEstateDTO).targetAnnex!.exteriorWallsCladdingAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;

          // precondition2 = true;
          if ((requestDTO as RebuildEstateDTO).targetAnnex!.roofTypes.includes('FLATROOF')) {
            precondition2 =
              precondition2 &&
              (requestDTO as RebuildEstateDTO).targetAnnex!.flatRoofsAboveTerrain.filter(
                (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
              ).length > 0;
          }
          // precondition3 = true;
          if ((requestDTO as RebuildEstateDTO).targetAnnex!.roofTypes.includes('INCLINEDROOF')) {
            precondition3 =
              precondition3 &&
              (requestDTO as RebuildEstateDTO).targetAnnex!.inclinedRoofsAboveTerrain.filter(
                (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
              ).length > 0;
          }
          precondition4 =
            precondition4 &&
            (requestDTO as RebuildEstateDTO).targetAnnex!.groundFloorSlabs.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;

          this.sharedControllerFnService.cleanUsages((requestDTO as RebuildEstateDTO).targetAnnex!.usages);

          if (
            (requestDTO as RebuildEstateDTO).targetAnnex!['elevatorInside'] != null &&
            (requestDTO as RebuildEstateDTO).targetAnnex!['elevatorInside'].vertical == null &&
            (requestDTO as RebuildEstateDTO).targetAnnex!['elevatorInside'].placeholder != null
          ) {
            (requestDTO as RebuildEstateDTO).targetAnnex!['elevatorInside'].vertical = (
              requestDTO as RebuildEstateDTO
            ).targetAnnex!['elevatorInside'].placeholder;
          }
        }

        let precondition5 = false;
        if ('targetType' in request && request.targetType) {
          precondition5 =
            requestDTO.energy.current.hotWaterFromHeating != null &&
            requestDTO.energy.target!.hotWaterFromHeating != null;
        }

        this.sharedControllerFnService.cleanUsages(requestDTO.usages);

        if (precondition1 && precondition2 && precondition3 && precondition4 && precondition5) {
          return this.httpClient
            .post<RebuildEstimationOutputDTO>(`${environment.serverUrl}/api/withdrawal/energy/needs2`, req)
            .pipe(
              untilDestroyed(this),
              takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),

              map((energyNeeds: RebuildEstimationOutputDTO) => {
                // if ('targetType' in request && request.targetType) {
                //   energyNeeds.targetType = request.targetType;
                // }
                return RealEstateActions.estimateOverhaulLCAEnergyNeedsSuccess(energyNeeds);
              }),
              catchError((error) => {
                console.warn('/api/withdrawal/energy/needs error: ', error);
                return of(RealEstateActions.estimateLCAEnergyNeedsFailure(error));
              }),
            );
        } else {
          return of(
            RealEstateActions.estimateLCAEnergyNeedsNoop({
              preconditions: [precondition1, precondition2, precondition3, precondition4],
            }),
          );
        }
      }),
    );
  });

  estimateEnergyPerformance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RealEstateActions.estimateLCAEnergyPerformance,
        RealEstateActions.estimateLCAEnergyNeedsAndPerformance,
        RealEstateActions.estimateLCAEnergyNeedsSuccess,
      ),
      debounceTime(200),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateWithStatus)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      switchMap(([request, filters]) => {
        if (
          filters.status === CALCULATION_STATE.CALCULATING ||
          filters.status === CALCULATION_STATE.FINAL_RESULT_LOADED
        ) {
          return of(RealEstateActions.noopDueToStatus());
        }

        const requestDTO = _.cloneDeep(filters.realEstate);
        const req = {
          realEstateDTO: requestDTO,
          lcaTargetType: 'targetType' in request ? request.targetType : 'CURRENT', // We send CURRENT for the normal LCA
        };

        // FIXME: This is here so that the energy.current is not null - otherwise we get error 500
        // Should we handle it somewhere else? Why do we need to empty the energy.current?
        // if ('targetType' in request && !request.targetType) {
        requestDTO.energy.current = {} as EnergyContainer;
        // }

        const precondition1 =
          requestDTO.quality.exteriorWallsCladdingAboveTerrain.filter(
            (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
          ).length > 0;

        let precondition2 = true;
        if (requestDTO.quality.roofTypes.includes('FLATROOF')) {
          precondition2 =
            requestDTO.quality.flatRoofsAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;
        }
        let precondition3 = true;
        if (requestDTO.quality.roofTypes.includes('INCLINEDROOF')) {
          precondition3 =
            requestDTO.quality.inclinedRoofsAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;
        }
        const precondition4 =
          requestDTO.quality.groundFloorSlabs.filter(
            (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
          ).length > 0;

        if (requestDTO.geometry.elevator != null && requestDTO.geometry.elevator.placeholder != null) {
          if (requestDTO.geometry.elevator['verticalDecision'] == 'no') {
            requestDTO.geometry.elevator.vertical = 0;
          } else if (!requestDTO.geometry.elevator.vertical) {
            requestDTO.geometry.elevator.vertical = requestDTO.geometry.elevator.placeholder;
          }
        }

        let precondition5 = false;
        if ('targetType' in request && request.targetType) {
          precondition5 =
            requestDTO.energy.current.hotWaterFromHeating != null &&
            requestDTO.energy.target!.hotWaterFromHeating != null;
        } else {
          // In case of normal LCA we don't need the hotWaterFromHeating so we set this to true - otherwise the API will not be called
          precondition5 = true;
        }

        this.sharedControllerFnService.cleanUsages(requestDTO.usages);

        if (precondition1 && precondition2 && precondition3 && precondition4 && precondition5) {
          return this.httpClient
            .post<RebuildEstimationOutputDTO>(`${environment.serverUrl}/api/withdrawal/energy/performance2`, req)
            .pipe(
              untilDestroyed(this),
              takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),
              map((data) => {
                return RealEstateActions.estimateLCAEnergyNeedsAndPerformanceSuccess({ data });
              }),
              catchError((error) => of(RealEstateActions.estimateLCAEnergyPerformanceFailure(error))),
            );
        } else {
          return of(
            RealEstateActions.estimateLCAEnergyNeedsAndPerformanceNoop({
              preconditions: [precondition1, precondition2, precondition3, precondition4],
            }),
          );
        }
      }),
    );
  });

  estimateOverhaulEnergyPerformance$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        RealEstateActions.estimateOverhaulLCAEnergyPerformance,
        RealEstateActions.estimateOverhaulLCAEnergyNeedsAndPerformance,
        RealEstateActions.estimateOverhaulLCAEnergyNeedsSuccess,
      ),
      debounceTime(200),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateWithStatus)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      switchMap(([request, filters]) => {
        if (
          filters.status === CALCULATION_STATE.CALCULATING ||
          filters.status === CALCULATION_STATE.FINAL_RESULT_LOADED
        ) {
          return of(RealEstateActions.noopDueToStatus());
        }

        const requestDTO = 'data' in request ? _.cloneDeep(request.data) : _.cloneDeep(filters.realEstate);
        const req = {
          realEstateDTO: requestDTO,
          lcaTargetType: 'targetType' in request ? request.targetType : 'OVERHAUL',
        };

        if (requestDTO.energy.target) {
          requestDTO.energy.target.annualTransportNeedsPlaceholder = null;
          requestDTO.energy.target.annualLightningNeedsPlaceholder = null;
          requestDTO.energy.target.annualApplianceNeedsPlaceholder = null;
          requestDTO.energy.target.annualDhwNeedsPlaceholder = null;
        }

        let precondition1 = false;
        let precondition2 = false;
        let precondition3 = false;
        let precondition4 = false;

        precondition1 =
          (requestDTO as RebuildEstateDTO).targetOverhaul.exteriorWallsCladdingAboveTerrain.filter(
            (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
          ).length > 0;

        precondition2 = true;
        if ((requestDTO as RebuildEstateDTO).targetOverhaul.roofTypes.includes('FLATROOF')) {
          precondition2 =
            (requestDTO as RebuildEstateDTO).targetOverhaul.flatRoofsAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;
        }
        precondition3 = true;
        if ((requestDTO as RebuildEstateDTO).targetOverhaul.roofTypes.includes('INCLINEDROOF')) {
          precondition3 =
            (requestDTO as RebuildEstateDTO).targetOverhaul.inclinedRoofsAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;
        }
        precondition4 =
          (requestDTO as RebuildEstateDTO).targetOverhaul.groundFloorSlabs.filter(
            (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
          ).length > 0;

        this.sharedControllerFnService.cleanUsages((requestDTO as RebuildEstateDTO).targetOverhaul.usages);

        if (
          (requestDTO as RebuildEstateDTO).targetOverhaul['elevatorInside'] != null &&
          (requestDTO as RebuildEstateDTO).targetOverhaul['elevatorInside'].vertical == null &&
          (requestDTO as RebuildEstateDTO).targetOverhaul['elevatorInside'].placeholder != null
        ) {
          if ((requestDTO as RebuildEstateDTO).targetOverhaul['elevatorInside']['verticalDecision'] == 'no') {
            (requestDTO as RebuildEstateDTO).targetOverhaul['elevatorInside'].vertical = 0;
          } else {
            (requestDTO as RebuildEstateDTO).targetOverhaul['elevatorInside'].vertical = (
              requestDTO as RebuildEstateDTO
            ).targetOverhaul['elevatorInside'].placeholder;
          }
        }

        if ((requestDTO as RebuildEstateDTO).selectedTemplates.includes('ADDITION_STORY')) {
          precondition1 =
            precondition1 &&
            (requestDTO as RebuildEstateDTO).targetAdditionStory!.exteriorWallsCladdingAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;

          // precondition2 = true;
          if ((requestDTO as RebuildEstateDTO).targetAdditionStory!.roofTypes.includes('FLATROOF')) {
            precondition2 =
              precondition2 &&
              (requestDTO as RebuildEstateDTO).targetAdditionStory!.flatRoofsAboveTerrain.filter(
                (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
              ).length > 0;
          }
          // precondition3 = true;
          if ((requestDTO as RebuildEstateDTO).targetAdditionStory!.roofTypes.includes('INCLINEDROOF')) {
            precondition3 =
              precondition3 &&
              (requestDTO as RebuildEstateDTO).targetAdditionStory!.inclinedRoofsAboveTerrain.filter(
                (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
              ).length > 0;
          }
          // precondition4 = true;
          this.sharedControllerFnService.cleanUsages((requestDTO as RebuildEstateDTO).targetAdditionStory!.usages);
        }

        if ((requestDTO as RebuildEstateDTO).selectedTemplates.includes('ANNEX')) {
          precondition1 =
            precondition1 &&
            (requestDTO as RebuildEstateDTO).targetAnnex!.exteriorWallsCladdingAboveTerrain.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;

          // precondition2 = true;
          if ((requestDTO as RebuildEstateDTO).targetAnnex!.roofTypes.includes('FLATROOF')) {
            precondition2 =
              precondition2 &&
              (requestDTO as RebuildEstateDTO).targetAnnex!.flatRoofsAboveTerrain.filter(
                (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
              ).length > 0;
          }
          // precondition3 = true;
          if ((requestDTO as RebuildEstateDTO).targetAnnex!.roofTypes.includes('INCLINEDROOF')) {
            precondition3 =
              precondition3 &&
              (requestDTO as RebuildEstateDTO).targetAnnex!.inclinedRoofsAboveTerrain.filter(
                (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
              ).length > 0;
          }
          precondition4 =
            precondition4 &&
            (requestDTO as RebuildEstateDTO).targetAnnex!.groundFloorSlabs.filter(
              (w) => w.area != null && (w.uvalue != null || w.uvaluePlaceholder != null),
            ).length > 0;

          this.sharedControllerFnService.cleanUsages((requestDTO as RebuildEstateDTO).targetAnnex!.usages);

          if (
            (requestDTO as RebuildEstateDTO).targetAnnex!['elevatorInside'] != null &&
            (requestDTO as RebuildEstateDTO).targetAnnex!['elevatorInside'].vertical == null &&
            (requestDTO as RebuildEstateDTO).targetAnnex!['elevatorInside'].placeholder != null
          ) {
            if ((requestDTO as RebuildEstateDTO).targetAnnex!['elevatorInside']['verticalDecision'] == 'no') {
              (requestDTO as RebuildEstateDTO).targetAnnex!['elevatorInside'].vertical = 0;
            } else {
              (requestDTO as RebuildEstateDTO).targetAnnex!['elevatorInside'].vertical = (
                requestDTO as RebuildEstateDTO
              ).targetAnnex!['elevatorInside'].placeholder;
            }
          }
        }

        let precondition5 = false;
        if ('targetType' in request && request.targetType) {
          precondition5 =
            requestDTO.energy.current.hotWaterFromHeating != null &&
            requestDTO.energy.target!.hotWaterFromHeating != null;
        }

        this.sharedControllerFnService.cleanUsages(requestDTO.usages);

        if (precondition1 && precondition2 && precondition3 && precondition4 && precondition5) {
          return this.httpClient
            .post<RebuildEstimationOutputDTO>(`${environment.serverUrl}/api/withdrawal/energy/performance2`, req)
            .pipe(
              untilDestroyed(this),
              takeUntil(this.actions$.pipe(ofType(RealEstateActions.estimateLCACosts))),
              map((data) => {
                // if ('targetType' in request && request.targetType) {
                //   data.targetType = request.targetType;
                // }
                return RealEstateActions.estimateOverhaulLCAEnergyNeedsAndPerformanceSuccess({ data });
              }),
              catchError((error) => of(RealEstateActions.estimateLCAEnergyPerformanceFailure(error))),
            );
        } else {
          return of(
            RealEstateActions.estimateLCAEnergyNeedsAndPerformanceNoop({
              preconditions: [precondition1, precondition2, precondition3, precondition4],
            }),
          );
        }
      }),
    );
  });

  estimateGreyEnergy$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.estimateLCAGreyEnergy),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateProgress)),
      switchMap(([request, filters]) =>
        this.httpClient.post(`${environment.serverUrl}/api/withdrawal/energy/grey`, filters).pipe(
          untilDestroyed(this),
          map((greyEnergyResult: any) => {
            return RealEstateActions.estimateLCAGreyEnergySuccess(greyEnergyResult);
          }),
          catchError((error) => of(RealEstateActions.estimateLCAGreyEnergyFailure(error))),
        ),
      ),
    );
  });

  plotCRREMCO2$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.estimateLCACostsSuccess, RealEstateActions.estimateLCACostsSuccessRebuild),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectWithdrawDTO)),
      concatMap(([request, filters]) => {
        // If it's a rebuild action, use the action data directly
        if (request.type === RealEstateActions.estimateLCACostsSuccessRebuild.type) {
          var payload = {
            ...request.outputDTO.lcaOutputs.lcaRebuildOutputPlot.crremPlotCO2Dto,
            width: 500,
            height: 300,
          };

          const headers = new HttpHeaders().set('content-type', 'application/json').append('Accept', 'image/svg+xml');

          return this.httpClient
            .post(`${environment.kvPlotService}/crrem-co2`, payload, { headers, responseType: 'blob' })
            .pipe(
              untilDestroyed(this),
              retry({ count: 3, delay: 1500 }),
              switchMap((image: Blob) => {
                const blobToBase64 = (blob: Blob): Promise<string> => {
                  const reader = new FileReader();
                  reader.readAsDataURL(blob);
                  return new Promise((resolve) => {
                    reader.onloadend = () => {
                      resolve(reader.result as string);
                    };
                  });
                };
                return from(blobToBase64(image));
              }),
              map((base64: string) => RealEstateActions.getLCAPlotCRREMCO2Success({ base64, targetType: 'OVERHAUL' })),
              catchError((error) => of(RealEstateActions.getLCAPlotCRREMCO2Error(error))),
            );
        }

        // For non-rebuild actions, use store data
        var payload = {
          ...filters.outputDTO.lcaOutputs.lcaOutputPlot.crremPlotCO2Dto,
          width: 500,
          height: 300,
        };

        const headers = new HttpHeaders().set('content-type', 'application/json').append('Accept', 'image/svg+xml');

        return this.httpClient
          .post(`${environment.kvPlotService}/crrem-co2`, payload, { headers, responseType: 'blob' })
          .pipe(
            untilDestroyed(this),
            retry({ count: 3, delay: 1500 }),
            switchMap((image: Blob) => {
              const blobToBase64 = (blob: Blob): Promise<string> => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                return new Promise((resolve) => {
                  reader.onloadend = () => {
                    resolve(reader.result as string);
                  };
                });
              };
              return from(blobToBase64(image));
            }),
            map((base64: string) => RealEstateActions.getLCAPlotCRREMCO2Success({ base64 })),
            catchError((error) => of(RealEstateActions.getLCAPlotCRREMCO2Error(error))),
          );
      }),
    );
  });

  plotNonRenewableEnergy$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.estimateLCACostsSuccess, RealEstateActions.estimateLCACostsSuccessRebuild),
      // ofType(RealEstateActions.getLCAPlotCRREMNonRenewableEnergy),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectWithdrawDTO)),
      concatMap(([request, filters]) => {
        // If it's a rebuild action, use the action data directly
        if (request.type === RealEstateActions.estimateLCACostsSuccessRebuild.type) {
          var payload = {
            ...request.outputDTO.lcaOutputs.lcaRebuildOutputPlot.crremPlotPNRDto,
            width: 500,
            height: 300,
          };

          const headers = new HttpHeaders()
            .set('content-type', 'application/json')
            // .set('Access-Control-Allow-Origin', '*')
            .append('Accept', 'image/svg+xml');
          return this.httpClient
            .post(`${environment.kvPlotService}/crrem-energy`, payload, { headers, responseType: 'blob' })
            .pipe(
              untilDestroyed(this),
              retry({ count: 3, delay: 1500 }),
              switchMap((image: Blob) => {
                const blobToBase64 = (blob: Blob): Promise<string> => {
                  const reader = new FileReader();
                  reader.readAsDataURL(blob);
                  return new Promise((resolve) => {
                    reader.onloadend = () => {
                      resolve(reader.result as string);
                    };
                  });
                };
                return from(blobToBase64(image));
              }),
              map((base64: string) =>
                RealEstateActions.getLCAPlotCRREMNonRenewableEnergySuccess({ base64, targetType: 'OVERHAUL' }),
              ),
              catchError((error) => of(RealEstateActions.getLCAPlotCRREMNonRenewableEnergyError(error))),
            );
        }

        var payload = {
          ...filters.outputDTO.lcaOutputs.lcaOutputPlot.crremPlotPNRDto,
          width: 500,
          height: 300,
        };
        const headers = new HttpHeaders()
          .set('content-type', 'application/json')
          // .set('Access-Control-Allow-Origin', '*')
          .append('Accept', 'image/svg+xml');
        return this.httpClient
          .post(`${environment.kvPlotService}/crrem-energy`, payload, { headers, responseType: 'blob' })
          .pipe(
            untilDestroyed(this),
            retry({ count: 3, delay: 1500 }),
            switchMap((image: Blob) => {
              const blobToBase64 = (blob: Blob): Promise<string> => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                return new Promise((resolve) => {
                  reader.onloadend = () => {
                    resolve(reader.result as string);
                  };
                });
              };
              return from(blobToBase64(image));
            }),
            map((base64: string) => RealEstateActions.getLCAPlotCRREMNonRenewableEnergySuccess({ base64 })),
            catchError((error) => of(RealEstateActions.getLCAPlotCRREMNonRenewableEnergyError(error))),
          );
      }),
    );
  });

  plotYearlyEnergy$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.estimateLCACostsSuccess, RealEstateActions.estimateLCACostsSuccessRebuild),
      // ofType(RealEstateActions.getLCAPlotCRREMNonRenewableEnergy),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectWithdrawDTO)),
      concatMap(([request, filters]) => {
        // If it's a rebuild action, use the action data directly
        if (request.type === RealEstateActions.estimateLCACostsSuccessRebuild.type) {
          const deepCopyEnergyPlotDTO = _.cloneDeep(filters.outputDTO.lcaOutputs.lcaOutputPlot.energyPlotDTO);
          const deepCopyRebuildEnergyPlotDTO = _.cloneDeep(
            request.outputDTO.lcaOutputs.lcaRebuildOutputPlot.energyPlotDTO,
          );
          deepCopyRebuildEnergyPlotDTO.forEach((element) => {
            element.labelByYear = '';
            element.width = 150;
          });
          var payload = deepCopyRebuildEnergyPlotDTO;
          // width: 500,
          // height: 300,
          payload[0].ymax = this.sharedControllerFnService.requestSvgEnergyNeedsCalcMax(
            deepCopyEnergyPlotDTO,
            deepCopyRebuildEnergyPlotDTO,
          );

          const headers = new HttpHeaders()
            .set('content-type', 'application/json')
            // .set('Access-Control-Allow-Origin', '*')
            .append('Accept', 'image/svg+xml');
          return (
            this.httpClient
              .post(`${environment.kvPlotService}/energy-needs-annual`, payload, { headers, responseType: 'blob' })
              // .post(`http:localhost:3000/energy-needs-annual`, payload, { headers, responseType: 'blob' })
              .pipe(
                untilDestroyed(this),
                retry({ count: 3, delay: 1500 }),
                switchMap((image: Blob) => {
                  const blobToBase64 = (blob: Blob): Promise<string> => {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    return new Promise((resolve) => {
                      reader.onloadend = () => {
                        resolve(reader.result as string);
                      };
                    });
                  };
                  return from(blobToBase64(image));
                }),
                map((base64: string) =>
                  RealEstateActions.getLCAPlotAnnualEnergySuccess({ base64, targetType: 'OVERHAUL' }),
                ),
                catchError((error) => of(RealEstateActions.getLCAPlotAnnualEnergyError(error))),
              )
          );
        }
        const deepCopyEnergyPlotDTO = _.cloneDeep(filters.outputDTO.lcaOutputs.lcaOutputPlot.energyPlotDTO);
        deepCopyEnergyPlotDTO.forEach((element) => {
          element.labelByYear = '';
          element.width = 150;
        });
        var payload = deepCopyEnergyPlotDTO;
        // width: 500,
        // height: 300,

        const headers = new HttpHeaders()
          .set('content-type', 'application/json')
          // .set('Access-Control-Allow-Origin', '*')
          .append('Accept', 'image/svg+xml');
        return (
          this.httpClient
            .post(`${environment.kvPlotService}/energy-needs-annual`, payload, { headers, responseType: 'blob' })
            // .post(`http:localhost:3000/energy-needs-annual`, payload, { headers, responseType: 'blob' })
            .pipe(
              untilDestroyed(this),
              retry({ count: 3, delay: 1500 }),
              switchMap((image: Blob) => {
                const blobToBase64 = (blob: Blob): Promise<string> => {
                  const reader = new FileReader();
                  reader.readAsDataURL(blob);
                  return new Promise((resolve) => {
                    reader.onloadend = () => {
                      resolve(reader.result as string);
                    };
                  });
                };
                return from(blobToBase64(image));
              }),
              map((base64: string) => RealEstateActions.getLCAPlotAnnualEnergySuccess({ base64 })),
              catchError((error) => of(RealEstateActions.getLCAPlotAnnualEnergyError(error))),
            )
        );
      }),
    );
  });

  plotGEAK$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.estimateLCACostsSuccess, RealEstateActions.estimateLCACostsSuccessRebuild),
      // ofType(RealEstateActions.getLCAPlotCRREMNonRenewableEnergy),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectWithdrawDTO)),
      concatMap(([request, filters]) => {
        // If it's a rebuild action, use the action data directly
        if (request.type === RealEstateActions.estimateLCACostsSuccessRebuild.type) {
          const deepCopyEnergyPlotDTO = _.cloneDeep(request.outputDTO.lcaOutputs.lcaOutputPlot.geakPlotDTO);
          const deepCopyEnergyPlotDTORebuild = _.cloneDeep(
            request.outputDTO.lcaOutputs.lcaRebuildOutputPlot.geakPlotDTO,
          );
          deepCopyEnergyPlotDTORebuild.forEach((element, index) => {
            if (element.main) {
              element.main = true;
              element.second = false;
            }
            // Set main to true in rebuild if it's true in the original
            if (deepCopyEnergyPlotDTO[index]?.main) {
              element.second = true;
            }
            element.height = 50;
            element.width = 355;
          });
          var payload = deepCopyEnergyPlotDTORebuild;

          const headers = new HttpHeaders()
            .set('content-type', 'application/json')
            // .set('Access-Control-Allow-Origin', '*')
            .append('Accept', 'image/svg+xml');
          return (
            this.httpClient
              .post(`${environment.kvPlotService}/geak`, payload, { headers, responseType: 'blob' })
              // .post(`http:localhost:3000/energy-needs-annual`, payload, { headers, responseType: 'blob' })
              .pipe(
                untilDestroyed(this),
                retry({ count: 3, delay: 1500 }),
                switchMap((image: Blob) => {
                  const blobToBase64 = (blob: Blob): Promise<string> => {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    return new Promise((resolve) => {
                      reader.onloadend = () => {
                        resolve(reader.result as string);
                      };
                    });
                  };
                  return from(blobToBase64(image));
                }),
                map((base64: string) => RealEstateActions.getLCAPlotGEAKSuccess({ base64 })),
                catchError((error) => of(RealEstateActions.getLCAPlotGEAKError(error))),
              )
          );
        }
        const deepCopyEnergyPlotDTO = _.cloneDeep(filters.outputDTO.lcaOutputs.lcaOutputPlot.geakPlotDTO);
        deepCopyEnergyPlotDTO.forEach((element) => {
          element.height = 50;
          element.width = 355;
        });
        var payload = deepCopyEnergyPlotDTO;

        const headers = new HttpHeaders()
          .set('content-type', 'application/json')
          // .set('Access-Control-Allow-Origin', '*')
          .append('Accept', 'image/svg+xml');
        return (
          this.httpClient
            .post(`${environment.kvPlotService}/geak`, payload, { headers, responseType: 'blob' })
            // .post(`http:localhost:3000/energy-needs-annual`, payload, { headers, responseType: 'blob' })
            .pipe(
              untilDestroyed(this),
              retry({ count: 3, delay: 1500 }),
              switchMap((image: Blob) => {
                const blobToBase64 = (blob: Blob): Promise<string> => {
                  const reader = new FileReader();
                  reader.readAsDataURL(blob);
                  return new Promise((resolve) => {
                    reader.onloadend = () => {
                      resolve(reader.result as string);
                    };
                  });
                };
                return from(blobToBase64(image));
              }),
              map((base64: string) => RealEstateActions.getLCAPlotGEAKSuccess({ base64 })),
              catchError((error) => of(RealEstateActions.getLCAPlotGEAKError(error))),
            )
        );
      }),
    );
  });

  presetGeoMetrics$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RealEstateActions.estimateGeoMetrics),
      //we don't want to blast the preset API with calls - so we add a debounce to at least channel  few updates together - this happends mainly when loading a object - or rapid updates on the form.
      debounceTime(200),
      concatLatestFrom(() => this.store.select(realEstatesFeature.selectRealEstateProgress)),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      switchMap(([request, filters]) => {
        const requestDTO = _.cloneDeep(filters);
        console.log('presetGeoMetrics$ - requestDTO: ', requestDTO);
        return this.httpClient
          .post<RealEstateDTO>(`${environment.serverUrl}/api/withdrawal/geo-metrics/estimate`, requestDTO)
          .pipe(
            untilDestroyed(this),

            map((realEstateDTO: RealEstateDTO) => {
              return RealEstateActions.estimateGeoMetricsSuccess({ data: realEstateDTO });
            }),
            catchError((error) => {
              console.warn('/api/withdrawal/geo-metrics/estimate error: ', error);
              return of(RealEstateActions.estimateGeoMetricsError(error));
            }),
          );
      }),
    );
  });

  private postProcessEmptyRealEstateObject(realEstateObj: OutRealEstateDTO, wtp: WithdrawalProductType) {
    switch (wtp) {
      case 'OPERATING_COSTS':
        realEstateObj.additions.length = 0;
        realEstateObj.additions.push({} as AdditionEntryDTO);
        break;

      case 'INSURANCE_VALUES_GVBS':
        realEstateObj.additions.push({
          label: 'Reserve',
          bkp: null,
        } as AdditionEntryDTO);
        break;

      default:
        realEstateObj.additions.push({
          label: this._translocoService.translate('modelrebuild.rebuild.additions.reserve'),
          bkp: 'BKP_6_RESERVE',
        } as AdditionEntryDTO);
        realEstateObj.additions.push({
          label: '',
          bkp: null,
        } as AdditionEntryDTO);
    }

    realEstateObj.energy = {
      current: {
        pvPeakPower: null,
        pvPeakPowerUserInput: null,
        pvPeakPowerUserPlaceholder: null,
        pvExport: null,
        hotWaterFromHeating: null,
        heatPowerUserInput: null,
        batteryCapacityUserInput: null,
        batteryCapacityPlaceholder: null,
        annualPvRoofProduction: null,
        annualPvRoofProductionUserInput: null,
        annualPvFacadeProduction: null,
        annualPvFacadeProductionUserInput: null,
        energySource: null,
        annualHeatingNeeds: null,
        annualHeatingNeedsPlaceholder: null,
        annualHeatingNeedsUserInput: null,
        annualCoolingNeeds: null,
        annualCoolingNeedsPlaceholder: null,
        annualCoolingNeedsUserInput: null,
        annualDhwNeeds: null,
        annualDhwNeedsPlaceholder: null,
        annualDhwNeedsUserInput: null,
        annualThermalSolarProduction: null,
        annualLightningNeeds: null,
        annualLightningNeedsPlaceholder: null,
        annualLightningNeedsUserInput: null,
        annualApplianceNeedsUserInput: null,
        annualApplianceNeeds: null,
        annualApplianceNeedsPlaceholder: null,
        annualNonEPBNeedsUserInput: null,
        annualNonEPBNeeds: null,
        annualNonEPBNeedsPlaceholder: null,
        annualTransportNeedsUserInput: null,
        annualTransportNeeds: null,
        annualTransportNeedsPlaceholder: null,
        annualDehumidificationNeeds: null,
        annualDehumidificationNeedsPlaceholder: null,
        annualDehumidificationNeedsUserInput: null,
        annualHumidificationNeeds: null,
        annualHumidificationNeedsPlaceholder: null,
        annualHumidificationNeedsUserInput: null,
        pvRoofProductionEnabled: null,
        pvFacadeProductionEnabled: null,
        batteryCapacityEnabled: null,
      },
      target: undefined,
    };
    realEstateObj.usages.push({
      type: null,
      standard: null,
      percentage: null,
      numFuUsage: null,
      comfortClass: 'CC3',
    } as RealEstateUsageEmbeddable);

    realEstateObj.quality.facadeTypes = [
      // {
      //   facadeType: 'MASSIVBAU_EDEL',
      //   uValue: 1,
      //   sqrMeter: 2016,
      //   orientation: 'SEV',
      // },
    ];

    realEstateObj.quality.heatingContainers = [
      // {
      //   heatingType: 'HEAT_PUMP_GEOTHERMAL',
      //   distribution: 'WUE_2',
      //   percentage: 100,
      //   fossileAmount: null,
      // },
    ];

    realEstateObj.quality.roofTypes = [];
  }
}
